import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, XCircle } from 'lucide-react';
import api from '@/services/apiClient';
import useAuthStore from '@/store/authStore';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Spinner from '@/components/ui/Spinner';

const money = (n) =>
  n == null ? '—' : `R$ ${Number(n).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const TIER_LABEL = { starter: 'Starter', pro: 'Pro', elite: 'Elite', ai: 'Movez AI' };

const STATUS_VARIANT = {
  active: 'success',
  canceled: 'neutral',
  past_due: 'danger',
};

/** Seção "Plano" do personal: mostra plano atual, permite assinar/trocar e cancelar. */
export default function PlanSection() {
  const { t } = useTranslation();
  const subscribe = useAuthStore((s) => s.subscribe);
  const cancelSubscription = useAuthStore((s) => s.cancelSubscription);

  const [plans, setPlans] = useState([]);
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [plansRes, meRes] = await Promise.all([
        api.get('/subscriptions/plans'),
        api.get('/subscriptions/me'),
      ]);
      const cat = plansRes.data || plansRes;
      setPlans(cat.saas || []);
      setSubs((meRes.data || meRes).subscriptions || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Assinatura SaaS ativa (plano atual do personal).
  const activeSaas = subs.find((s) => s.type === 'trainer_saas' && s.status === 'active');

  const onSubscribe = async (tier) => {
    setBusy(tier);
    setError('');
    try {
      await subscribe(tier);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  const onCancel = async (id) => {
    setBusy('cancel');
    setError('');
    try {
      await cancelSubscription(id);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return (
      <Card padding="lg">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-8)' }}>
          <Spinner />
        </div>
      </Card>
    );
  }

  return (
    <Card padding="lg">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 14,
        }}
      >
        <div className="kicker">{t('plan.title')}</div>
        {activeSaas && (
          <Badge variant={STATUS_VARIANT[activeSaas.status] || 'neutral'}>
            {TIER_LABEL[activeSaas.tier] || activeSaas.tier} ·{' '}
            {t(`plan.status.${activeSaas.status}`, activeSaas.status)}
          </Badge>
        )}
      </div>

      {activeSaas ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: 14,
            borderRadius: 'var(--radius-md)',
            background: 'var(--surf2)',
            marginBottom: 18,
          }}
        >
          <div>
            <div style={{ color: 'var(--hi)', fontWeight: 600, fontSize: 15 }}>
              {t('plan.currentPlan')}: {TIER_LABEL[activeSaas.tier] || activeSaas.tier}
            </div>
            <div style={{ color: 'var(--mid)', fontSize: 12.5, marginTop: 3 }}>
              {money(activeSaas.priceMonthly)}/{t('plan.perMonth')}
              {activeSaas.currentPeriodEnd &&
                ` · ${t('plan.renewsOn', {
                  date: new Date(activeSaas.currentPeriodEnd).toLocaleDateString('pt-BR'),
                })}`}
            </div>
          </div>
          <Button
            variant="outline"
            icon={XCircle}
            loading={busy === 'cancel'}
            onClick={() => onCancel(activeSaas.id)}
          >
            {t('plan.cancel')}
          </Button>
        </div>
      ) : (
        <div style={{ color: 'var(--mid)', fontSize: 13, marginBottom: 18 }}>
          {t('plan.noActivePlan')}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {plans.map((p) => {
          const isCurrent = activeSaas?.tier === p.tier;
          return (
            <div
              key={p.tier}
              style={{
                border: isCurrent
                  ? '1px solid color-mix(in srgb, var(--volt) 45%, transparent)'
                  : '1px solid var(--line)',
                borderRadius: 'var(--radius-md)',
                padding: 16,
                background: 'var(--surf)',
              }}
            >
              <div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 600,
                  fontSize: 16,
                  color: 'var(--hi)',
                }}
              >
                {TIER_LABEL[p.tier] || p.tier}
              </div>
              <div
                style={{
                  fontFamily: 'var(--font-display)',
                  fontWeight: 700,
                  fontSize: 22,
                  color: 'var(--hi)',
                  marginTop: 8,
                }}
              >
                {money(p.priceMonthly)}
                <span style={{ fontSize: 12, color: 'var(--mid)', fontWeight: 500 }}>
                  /{t('plan.perMonth')}
                </span>
              </div>
              <div style={{ color: 'var(--mid)', fontSize: 12.5, marginTop: 6 }}>
                {p.maxStudents == null
                  ? t('plan.unlimitedStudents')
                  : t('plan.upToStudents', { count: p.maxStudents })}
              </div>
              <div style={{ marginTop: 14 }}>
                {isCurrent ? (
                  <Button variant="ghost" icon={Check} fullWidth disabled>
                    {t('plan.currentPlanShort')}
                  </Button>
                ) : (
                  <Button
                    variant="outline"
                    fullWidth
                    loading={busy === p.tier}
                    onClick={() => onSubscribe(p.tier)}
                  >
                    {activeSaas ? t('plan.switch') : t('plan.subscribe')}
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <div style={{ color: 'var(--danger)', fontSize: 13, marginTop: 14 }}>{error}</div>
      )}
    </Card>
  );
}
